import { useEffect, useRef, useState } from "react";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import LinkIcon from "@mui/icons-material/Link";
import { TextBlock } from "../../../types/storyMap";
import { normaliseLinkUrl, sanitizeStoryHtml } from "../sanitizeStoryHtml";

interface TextBlockEditorProps {
  block: TextBlock;
  onChange: (patch: Partial<TextBlock>) => void;
}

/**
 * Small contentEditable editor with a bold/italic/link toolbar. HTML lives in
 * block.html. document.execCommand is deprecated but fine for v1.
 *
 * Everything this emits ends up rendered as HTML in the reader and the print
 * page, so it is sanitized on the way out (and on paste, so what the author
 * sees is what gets stored). Renderers sanitize again — this is convenience,
 * not the security boundary.
 */
export default function TextBlockEditor({ block, onChange }: TextBlockEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  // Uncontrolled: seed innerHTML when the block identity changes only, so the
  // caret isn't reset on every keystroke.
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== block.html) {
      editorRef.current.innerHTML = sanitizeStoryHtml(block.html);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  const emitChange = () => {
    if (editorRef.current) onChange({ html: sanitizeStoryHtml(editorRef.current.innerHTML) });
  };

  const exec = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    emitChange();
  };

  const handleLink = () => {
    const entered = window.prompt("Link URL");
    if (entered === null) return; // cancelled
    const url = normaliseLinkUrl(entered);
    if (url) {
      setLinkError(null);
      exec("createLink", url);
    } else {
      setLinkError("Links must be a web address (http, https) or an email address.");
    }
  };

  // Paste arrives as whatever markup the source page had. Insert the sanitized
  // form so the editor DOM matches what will be stored and rendered.
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const html = event.clipboardData.getData("text/html");
    if (!html) return; // plain text: let the browser handle it
    event.preventDefault();
    document.execCommand("insertHTML", false, sanitizeStoryHtml(html));
    emitChange();
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Tooltip title="Bold" arrow>
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")}>
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic" arrow>
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")}>
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Link" arrow>
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleLink}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
        sx={{
          minHeight: 72,
          outline: "none",
          fontSize: "0.9rem",
          lineHeight: 1.6,
          "& p": { m: 0, mb: 1 },
          "&:empty::before": {
            content: '"Write something…"',
            color: "text.disabled",
          },
        }}
      />
      {linkError && (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {linkError}
        </Typography>
      )}
    </Box>
  );
}
